import React from 'react';

import { connect } from 'react-redux';

import withLoyaltyAccess from '@/hocs/withLoyaltyAccess';
import { useTranslation } from '@/i18n';
import { CustomerAdvocacyBadgeItemType, BadgesPropsType } from '@/models/customerAdvocacyModels';
import { Paginator, ServerStatus } from '@/models/globalModels';
import { doGetCustomerAdvocacyBadges } from '@/redux/customerAdvocacy/actions';
import { RootState } from '@/redux/rootReducer';

import BadgeItem from './BadgeItem';

const mapDispatchToProps = {
  doGetCustomerAdvocacyBadges,
};

const mapStateToProps = (state: RootState) => {
  const auth = state.auth;
  const customerAdvocacy = state.customerAdvocacy;
  return {
    ambassadorProfile: auth.ambassadorProfile,
    badgesStatus: customerAdvocacy.badgesStatus,
    badgesData: customerAdvocacy.badgesData,
  };
};

const checkIfEarned = (earnedBadges: CustomerAdvocacyBadgeItemType[], i: CustomerAdvocacyBadgeItemType) => {
  return earnedBadges.some((b) => b.uid === i.uid);
};

const sortByPoints = (badges: CustomerAdvocacyBadgeItemType[], pointsKey: keyof CustomerAdvocacyBadgeItemType) => {
  return [...badges].sort((a, b) => (Number(a[pointsKey]) ?? 0) - (Number(b[pointsKey]) ?? 0));
};

const CustomerAdvocacyBadges = ({ ambassadorProfile, badgesStatus, badgesData, doGetCustomerAdvocacyBadges }: BadgesPropsType) => {
  const { t } = useTranslation('dashboard');
  const [badgesList, setBadgesList] = React.useState<Paginator<CustomerAdvocacyBadgeItemType>>();
  const profileBadges = ambassadorProfile ? ambassadorProfile.loyalty_badges : [];

  const sortedTierBadges = React.useMemo(() => {
    if (!badgesList) return [];
    const earnedBadges = badgesList.results.filter((badge) => badge.is_tier && badge.is_active && checkIfEarned(profileBadges, badge));
    const notEarnedBadges = badgesList.results.filter((badge) => badge.is_tier && badge.is_active && !checkIfEarned(profileBadges, badge));
    return [...sortByPoints(earnedBadges, 'point_threshold'), ...sortByPoints(notEarnedBadges, 'point_threshold')];
  }, [badgesList]);

  const sortedNonTierBadges = React.useMemo(() => {
    if (!badgesList) return [];
    const earnedBadges = badgesList.results.filter((badge) => !badge.is_tier && badge.is_active && checkIfEarned(profileBadges, badge));
    const notEarnedBadges = badgesList.results.filter((badge) => !badge.is_tier && badge.is_active && !checkIfEarned(profileBadges, badge));
    return [...sortByPoints(earnedBadges, 'points'), ...sortByPoints(notEarnedBadges, 'points')];
  }, [badgesList]);

  React.useEffect(() => {
    if (badgesStatus === ServerStatus.FETCH) {
      setBadgesList(badgesData);
    }
  }, [badgesStatus, badgesData]);

  React.useEffect(() => {
    doGetCustomerAdvocacyBadges();
  }, [doGetCustomerAdvocacyBadges]);

  return (
    <div className="Badges align-items-center justify-content-center">
      <div className="BadgesWrapper mt-3 f-raleway-regular">
        <h3 className="f-14 f-raleway-bold">{t('sidebar.badgesTitle')}</h3>
        <div className="badgeCollection p-0">
          {sortedTierBadges.map((badge) => (
            <BadgeItem
              key={badge.uid}
              badgeImage={badge.badge_image}
              color={badge.color}
              description={badge.description}
              earned={checkIfEarned(profileBadges, badge)}
              extraClass={badge.extraClass}
              name={badge.name}
              points={badge.point_threshold || 0}
            />
          ))}
          <hr />
          {sortedNonTierBadges.map((badge) => (
            <BadgeItem
              key={badge.uid}
              badgeImage={badge.badge_image}
              color={badge.color}
              description={badge.description}
              earned={checkIfEarned(profileBadges, badge)}
              extraClass={badge.extraClass}
              name={badge.name}
              points={badge.points || 0}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(withLoyaltyAccess(CustomerAdvocacyBadges));
